<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="modal-header">
        <h2>{{ isEditing ? $t('tables.editTable') : $t('tables.addTable') }}</h2>
        <button @click="$emit('close')" class="close-btn">
          <font-awesome-icon icon="fa-times" />
        </button>
      </div>

      <div class="modal-body">
        <form ref="formRef" @submit.prevent="saveTable">
          <div class="form-group">
            <label for="tablePrefix">{{ $t('tables.namePrefix') }} <span class="required">*</span></label>
            <input id="tablePrefix" v-model.trim="namePrefix" type="text" class="form-control"
              :placeholder="$t('tables.namePrefixPlaceholder')" />
          </div>

          <div class="form-group" data-field="tableNumber">
            <label for="tableNumber">{{ $t('tables.tableNumber') }} <span class="required">*</span></label>
            <input id="tableNumber" v-model.number="formData.number" type="number" class="form-control"
              :placeholder="$t('tables.tableNumberPlaceholder')" min="1" />
          </div>

          <div class="form-group">
            <label>{{ $t('tables.tableName') }}</label>
            <div class="form-control readonly-field">{{ normalizedPrefix }} {{ formData.number }}</div>
          </div>

          <div v-if="error" class="error-message">
            <font-awesome-icon icon="exclamation-circle" class="error-icon" />
            {{ error }}
          </div>

          <div class="form-group">
            <label for="tableCapacity">{{ $t('tables.capacity') }}</label>
            <input id="tableCapacity" v-model.number="formData.capacity" type="number" class="form-control"
              :placeholder="$t('tables.capacityPlaceholder')" min="0" />
          </div>

        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, reactive } from 'vue';
import { tableApi } from '@/services/api';
import { Table, TableRequest } from '@/types/tables';
import { useI18n } from 'vue-i18n';
import BaseButton from '@/components/shared/BaseButton.vue';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { useFormFloatingActions } from '@/composables/useFormFloatingActions';
import { scrollToFirstFormError, stopGuidedFormErrorNavigation } from '@/utils/scrollToFirstFormError';

const formRef = ref<HTMLFormElement | null>(null);
const formErrors = reactive<Record<string, string>>({
  tablePrefix: '',
  tableNumber: '',
});

const props = defineProps({
  table: {
    type: Object as () => Table | null,
    default: null
  },
  locationId: {
    type: String,
    required: true
  }
});

const emit = defineEmits(['close', 'saved']);
const { t } = useI18n();

const loading = ref(false);
const error = ref<string | null>(null);
const namePrefix = ref('Stol');

const isEditing = computed(() => props.table !== null);

const formData = ref<TableRequest>({
  name: '',
  number: 1,
  capacity: 0
});

const normalizedPrefix = computed(() => {
  return namePrefix.value.trim() || 'Stol';
});

onMounted(() => {
  if (props.table) {
    const matched = props.table.name.match(/^(.*?)(?:\s+(\d+))?$/);
    const detectedPrefix = matched?.[1]?.trim() || 'Stol';
    namePrefix.value = detectedPrefix;
    formData.value = {
      name: props.table.name,
      number: props.table.number,
      capacity: props.table.capacity
    };
  } else {
    formData.value.name = `${normalizedPrefix.value} ${formData.value.number}`;
  }
});

function validateForm(): boolean {
  formErrors.tablePrefix = '';
  formErrors.tableNumber = '';
  error.value = null;

  let isValid = true;
  if (!namePrefix.value.trim()) {
    formErrors.tablePrefix = t('validation.required');
    isValid = false;
  }
  if (!formData.value.number || formData.value.number < 1) {
    formErrors.tableNumber = t('forms.validationErrors.priceGreaterThanZero');
    isValid = false;
  }
  return isValid;
}

const saveTable = async () => {
  if (!validateForm()) {
    await scrollToFirstFormError(formErrors, {
      root: formRef.value,
      fieldOrder: ['tablePrefix', 'tableNumber'],
      fieldSelectorMap: {
        tablePrefix: '#tablePrefix',
        tableNumber: '#tableNumber',
      },
      validate: validateForm,
    });
    return;
  }

  stopGuidedFormErrorNavigation();

  loading.value = true;

  try {
    formData.value.name = `${normalizedPrefix.value} ${formData.value.number}`;

    if (isEditing.value && props.table) {
      await tableApi.updateTable(props.table.uuid, formData.value);
    } else {
      await tableApi.createTable(props.locationId, formData.value);
    }
    emit('saved');
  } catch (err: any) {
    error.value = getApiErrorMessage(err, t('tables.errorSavingTable'));
  } finally {
    loading.value = false;
  }
};

useFormFloatingActions({
  validate: validateForm,
  submit: saveTable,
  cancel: () => emit('close'),
  formErrors,
  getSubmitLabel: () => (isEditing.value ? t('general.save') : t('general.create')),
  getLoading: () => loading.value,
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: #6c757d;
}

.modal-body {
  padding: 1rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  position: relative;
}

.form-control {
  width: 100%;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.readonly-field {
  background-color: #f9fafb;
  color: #374151;
  font-weight: 600;
}

.error-message {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  color: #e53e3e;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #fff5f5;
  border-radius: 0.5rem;
  border-left: 3px solid #e53e3e;
  font-size: 0.95rem;
}

.error-icon {
  margin-top: 0.15rem;
}

.form-control:focus {
  border-color: #D8670C;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(216, 103, 12, 0.25);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
}
</style>